import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users,
  Plus,
  Search,
  Trash2,
  X,
  Shield,
  Mail,
  Phone,
  UserCheck,
  KeyRound,
  Eye,
  EyeOff,
  Building2,
  CheckCircle2,
  Lock,
  ArrowRight,
  Sparkles,
  Info
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { Link } from 'react-router-dom'

export default function UsersPage() {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const { activeRoleName, switchActiveRole, user: currentUser, tenant } = useAuthStore()

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role_name: 'صندوق‌دار'
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/users', { search }],
    queryFn: () => api.get('/users', { params: { search } }).then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (newUserData: typeof form) => api.post('/users', newUserData),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/users'] })
      toast.success(`حساب کاربری برای ${variables.full_name} با نقش ${variables.role_name} ایجاد شد`)
      setIsOpen(false)
      setForm({ full_name: '', email: '', phone: '', password: '', role_name: 'صندوق‌دار' })
    },
    onError: () => toast.error('خطا در ثبت کاربر جدید')
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/users'] })
      toast.success('کاربر با موفقیت حذف شد')
    },
    onError: () => toast.error('خطا در حذف کاربر')
  })

  const defaultUsersList = [
    {
      id: 'usr_1',
      full_name: 'مدیر فروشگاه',
      email: 'admin@kaseb.app',
      phone: '09123456789',
      role_name: 'مدیر سیستم',
      is_active: true,
      tenant_name: tenant?.name || 'فروشگاه کسب',
      created_at: '2026-07-01T00:00:00Z'
    },
    {
      id: 'usr_2',
      full_name: 'رضا کریمی (صندوق‌دار)',
      email: 'karimi@kaseb.app',
      phone: '09129876543',
      role_name: 'صندوق‌دار',
      is_active: true,
      tenant_name: tenant?.name || 'فروشگاه کسب',
      created_at: '2026-07-15T00:00:00Z'
    },
    {
      id: 'usr_3',
      full_name: 'سارا محمدی (انباردار)',
      email: 'mohammadi@kaseb.app',
      phone: '09125554433',
      role_name: 'انباردار',
      is_active: true,
      tenant_name: tenant?.name || 'فروشگاه کسب',
      created_at: '2026-07-20T00:00:00Z'
    },
    {
      id: 'usr_4',
      full_name: 'امیر رضایی (حسابدار)',
      email: 'rezaei@kaseb.app',
      phone: '09121118899',
      role_name: 'حسابدار',
      is_active: true,
      tenant_name: tenant?.name || 'فروشگاه کسب',
      created_at: '2026-07-25T00:00:00Z'
    }
  ]

  const items = data?.items && data.items.length > 0 ? data.items : defaultUsersList

  const filteredUsers = items.filter((u: any) =>
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role_name?.toLowerCase().includes(search.toLowerCase())
  )

  const handleLoginAsUser = (userItem: any) => {
    switchActiveRole(userItem.role_name)
    toast.success(`شما با نقش «${userItem.role_name}» (${userItem.full_name}) وارد پنل مجموعه ${tenant?.name || 'فروشگاه کسب'} شدید.`, {
      duration: 5000,
      icon: '🔐'
    })
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <Users className="w-7 h-7 text-primary-600 dark:text-primary-400" />
            مدیریت کاربران و پرسنل مجموعه
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            تعریف نام کاربری، رمز عبور و تخصیص نقش برای پرسنل متصل به «{tenant?.name || 'فروشگاه کسب'}»
          </p>
        </div>
        <Button onClick={() => setIsOpen(true)} className="gap-2 shadow-lg shadow-primary-500/20">
          <Plus className="w-4 h-4" />
          تعریف کاربر جدید (کارمند)
        </Button>
      </motion.div>

      {/* Info Card explaining how Users & Roles work in Tenant */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="card p-5 bg-gradient-to-r from-primary-900 via-neutral-900 to-neutral-900 text-white border border-primary-700/50 shadow-xl space-y-3"
      >
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-primary-500/20 text-primary-300 border border-primary-500/40">
            <Building2 className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <h3 className="font-bold text-base text-white flex items-center gap-2">
              نحوه عملکرد دسترسی پرسنل به مجموعه:
            </h3>
            <p className="text-xs text-neutral-300 leading-relaxed max-w-4xl">
              مدیر مجموعه (شما) ابتدا در بخش <strong>«نقش‌ها و دسترسی‌ها»</strong> چک‌باکس‌های مجاز (مانند ثبت فروش، دیدن موجودی، حسابداری) را علامت می‌زند. سپس برای هر کارمند نام کاربری و رمز تعریف می‌کند. هنگام ورود کارمند، به داده‌های این مجموعه متصل شده اما فقط بخش‌های تاییدشده در نقش وی فعال می‌باشند.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-neutral-800 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-neutral-400">نقش فعال فعلی شما:</span>
            <span className="px-2.5 py-1 rounded-full bg-primary-500 text-white font-bold">
              {activeRoleName}
            </span>
          </div>

          <Link to="/admin/roles">
            <Button size="sm" variant="outline" className="gap-1.5 text-xs text-white border-neutral-700 hover:bg-neutral-800">
              <Shield className="w-3.5 h-3.5 text-primary-400" />
              مدیریت چک‌باکس‌های دسترسی نقش‌ها
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="جستجوی نام، ایمیل یا نقش کاربر..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-10"
          />
        </div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
          <Info className="w-4 h-4 text-primary-500" />
          <span>تعداد پرسنل مجموعه: <strong className="text-neutral-900 dark:text-white font-bold">{items.length} نفر</strong></span>
        </div>
      </div>

      {/* Users Table */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="card p-4">
              <Skeleton className="h-5 w-48" />
            </div>
          ))}
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="card p-12 text-center space-y-3">
          <Users className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600" />
          <p className="text-neutral-600 dark:text-neutral-400 font-medium">کاربری پیدا نشد</p>
          <Button variant="outline" size="sm" onClick={() => setSearch('')}>پاک کردن جستجو</Button>
        </div>
      ) : (
        <div className="card overflow-hidden shadow-lg border border-neutral-200 dark:border-neutral-800">
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead className="bg-neutral-100 dark:bg-neutral-800/80 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th className="px-4 py-3 text-xs font-bold text-neutral-700 dark:text-neutral-300">نام و نام خانوادگی</th>
                  <th className="px-4 py-3 text-xs font-bold text-neutral-700 dark:text-neutral-300">ایمیل / نام کاربری</th>
                  <th className="px-4 py-3 text-xs font-bold text-neutral-700 dark:text-neutral-300">شماره تماس</th>
                  <th className="px-4 py-3 text-xs font-bold text-neutral-700 dark:text-neutral-300">نقش تخصیصی</th>
                  <th className="px-4 py-3 text-xs font-bold text-neutral-700 dark:text-neutral-300">مجموعه</th>
                  <th className="px-4 py-3 text-xs font-bold text-neutral-700 dark:text-neutral-300">وضعیت</th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-neutral-700 dark:text-neutral-300">عملیات ورود & مدیریت</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {filteredUsers.map((item: any) => {
                  const isUserActiveInRole = activeRoleName === item.role_name

                  return (
                    <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                      <td className="px-4 py-3 text-sm font-bold text-neutral-900 dark:text-white flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400 flex items-center justify-center font-bold text-sm shadow-sm">
                          {item.full_name?.[0] || 'ک'}
                        </div>
                        <div>
                          <span>{item.full_name}</span>
                          {item.role_name === 'مدیر سیستم' && (
                            <span className="block text-[10px] text-amber-600 dark:text-amber-400 font-semibold">مالک مجموعه</span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300 font-mono">
                        {item.email}
                      </td>

                      <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">
                        {item.phone || '---'}
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <Badge variant={item.role_name === 'مدیر سیستم' ? 'primary' : 'secondary'} className="font-bold">
                          {item.role_name || 'کاربر'}
                        </Badge>
                      </td>

                      <td className="px-4 py-3 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                        {tenant?.name || 'فروشگاه کسب'}
                      </td>

                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 text-xs font-bold">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                          فعال
                        </span>
                      </td>

                      <td className="px-4 py-3 text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant={isUserActiveInRole ? 'secondary' : 'outline'}
                            onClick={() => handleLoginAsUser(item)}
                            className="gap-1 text-xs"
                            title="ورود صوری با نام این کارمند جهت تست دقیق محدودیت‌ها"
                          >
                            {isUserActiveInRole ? (
                              <>
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                نقش این کاربر فعال است
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-3.5 h-3.5" />
                                ورود با نقش این کارمند
                              </>
                            )}
                          </Button>

                          {item.role_name !== 'مدیر سیستم' && (
                            <button
                              onClick={() => deleteMutation.mutate(item.id)}
                              className="p-1.5 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                              title="حذف کاربر"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Add New Employee */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-neutral-200 dark:border-neutral-800 space-y-4 my-8"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-primary-600" />
                    تعریف کارمند جدید در مجموعه
                  </h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    اتصال حساب به «{tenant?.name || 'فروشگاه کسب'}» با رمز عبور و نقش دلخواه
                  </p>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!form.full_name.trim()) {
                    toast.error('لطفاً نام و نام خانوادگی را وارد کنید')
                    return
                  }
                  if (!form.email.trim()) {
                    toast.error('لطفاً ایمیل / نام کاربری را وارد کنید')
                    return
                  }
                  createMutation.mutate(form)
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-neutral-800 dark:text-neutral-200 mb-1">
                    نام و نام خانوادگی کارمند <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="مانند: محمد رضایی"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-800 dark:text-neutral-200 mb-1">
                    ایمیل / نام کاربری ورود <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    placeholder="rezaei@kaseb.app"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-800 dark:text-neutral-200 mb-1">
                    شماره همراه
                  </label>
                  <Input
                    placeholder="09123456789"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-800 dark:text-neutral-200 mb-1">
                    رمز عبور کارمند
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="رمز عبور دلخواه (حداقل ۶ کاراکتر)"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-neutral-800 dark:text-neutral-200 mb-1">
                    انتخاب نقش و سطح دسترسی <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.role_name}
                    onChange={(e) => setForm({ ...form, role_name: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="صندوق‌دار">صندوق‌دار (فقط فروش و رسیدها)</option>
                    <option value="انباردار">انباردار (فقط مدیریت کالا و موجودی)</option>
                    <option value="حسابدار">حسابدار (گزارشات و فاکتورهای خرید)</option>
                    <option value="مدیر سیستم">مدیر سیستم (دسترسی کامل)</option>
                  </select>
                </div>

                <div className="p-3 bg-neutral-50 dark:bg-neutral-800/60 rounded-xl border border-neutral-200 dark:border-neutral-700 text-xs text-neutral-600 dark:text-neutral-300">
                  <Shield className="w-4 h-4 inline text-primary-500 ml-1" />
                  دسترسی‌های این کارمند بر اساس تنظیمات چک‌باکس‌های نقش <strong>«{form.role_name}»</strong> اعمال خواهد شد.
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-neutral-100 dark:border-neutral-800">
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={createMutation.isPending} className="gap-2">
                    <UserCheck className="w-4 h-4" />
                    ثبت و ساخت حساب کارمند
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
